import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Input del sistema de diseño.
 *
 * Estilo de la referencia (el buscador del header): radio de 10px, borde gris claro,
 * texto de 14px y placeholder gris tenue. El foco se marca con borde azul + halo suave
 * (`ring`) en lugar del outline por defecto.
 */

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Icono a la izquierda (ej. la lupa del buscador). */
  leftIcon?: ReactNode;
  /** Slot a la derecha (ej. un icono o un botón pequeño). */
  rightSlot?: ReactNode;
  /** Marca el campo en rojo (error de validación). */
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, leftIcon, rightSlot, invalid = false, ...props },
  ref,
) {
  return (
    <div className="relative w-full">
      {leftIcon ? (
        <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-faint">
          {leftIcon}
        </span>
      ) : null}

      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          'h-10 w-full rounded-control border bg-surface text-body text-ink transition-colors',
          'placeholder:text-ink-faint',
          'focus:outline-none focus-visible:outline-none focus-visible:ring-4',
          'disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-muted',
          invalid
            ? 'border-danger focus-visible:border-danger focus-visible:ring-danger-soft'
            : 'border-line-strong focus-visible:border-brand-500 focus-visible:ring-brand-100',
          leftIcon ? 'pl-9' : 'pl-3',
          rightSlot ? 'pr-10' : 'pr-3',
          className,
        )}
        {...props}
      />

      {rightSlot ? (
        <span className="absolute top-1/2 right-2 -translate-y-1/2">{rightSlot}</span>
      ) : null}
    </div>
  );
});
